import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Rune } from "@outplayed/tooltips";
import { HeaderBullet } from "@ugg/shared/components/common/HeaderBullet";
import { round } from "@ugg/shared/utils/math";
import { AdvBuildInfo } from "@ugg/shared/api/data-parser/champions/adv-builds";

interface RuneSetPairsProps {
  data: AdvBuildInfo["keystone_pairs"];
  patch: string;
  totalMatches: number;
  viewAll: boolean;
  setViewAll: React.Dispatch<React.SetStateAction<boolean>>;
  selectedRunePair: readonly [number, number] | null;
  setSelectedRunePair: React.Dispatch<React.SetStateAction<readonly [number, number] | null>>;
}

export function RuneSetPairs(props: RuneSetPairsProps) {
  const { t } = useTranslation();
  const { data, patch, totalMatches, viewAll, setViewAll, selectedRunePair, setSelectedRunePair } = props;

  const displayKeystones = () => {
    return data.map((el, index) => {
      const active =
        !viewAll && el.keystone_id === selectedRunePair?.[0] && el.sub_style === selectedRunePair?.[1] ? "active" : "";
      const { keystone_id, sub_style, win_rate, matches } = el;

      return (
        <div
          key={index}
          className={classNames(
            "flex justify-between px-[18px] py-[12px] bg-purple-200 border-t-[1px] border-purple-500 hover:bg-purple-400 cursor-pointer",
            {
              "bg-purple-400": active,
            },
          )}
          onClick={() => {
            setSelectedRunePair([keystone_id, sub_style]);
            setViewAll(false);
          }}
        >
          <div className="flex items-center">
            <Rune className="w-[48px] h-[48px]" runeId={keystone_id} patch={patch} disableTooltip ssr />
            <Rune className="w-[36px] h-[36px]" runeId={sub_style} patch={patch} disableTooltip ssr />
          </div>
          <div className="flex flex-col justify-center">
            <div className="grid grid-flow-col grid-cols-[1.3fr_1fr] w-[105px]">
              <span className="text-[12px] text-lavender-50">{t(`Pick Rate`)}: </span>
              <span className="text-[12px] text-white font-semibold text-right">{`${round(
                (matches / totalMatches) * 100,
                2,
              )}%`}</span>
            </div>
            <div className="grid grid-flow-col grid-cols-[1.3fr_1fr]">
              <span className="text-[12px] text-lavender-50">{`${t(`Win Rate`)}: `}</span>
              <span className="text-[12px] text-white font-semibold text-right">{`${win_rate}%`}</span>
            </div>
          </div>
        </div>
      );
    });
  };

  return (
    <div className="rounded-[3px] overflow-hidden">
      <div className="flex items-center justify-between p-[18px] bg-purple-200">
        <HeaderBullet>{t(`Keystones`)}</HeaderBullet>
        <div
          className="text-white text-[14px] font-bold hover:text-accent-blue-400 cursor-pointer"
          onClick={() => {
            setViewAll(true);
            setSelectedRunePair(null);
          }}
        >
          View All
        </div>
      </div>
      <div>{displayKeystones()}</div>
    </div>
  );
}
